import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Group, Table } from '@/types'
import { getTableHeight, getTableWidth } from '@/utils/geometry'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { useDiagramStore, useSelectionStore, useSettingsStore, useViewportStore } from '@/stores'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useDraftStore } from '@/stores/draftStore'
import { SCALE_MIN } from '@/stores/viewportStore'
import { SidebarGroup } from './SidebarGroup'

const NO_GROUP_KEY = '__none__'
const FALLBACK_GROUP_COLOR = '#94A3B8'
const TABLE_FOCUS_MAX_SCALE = 1
const TABLE_FOCUS_PADDING = 48

interface GroupBucket {
  key: string
  group: Group
  tables: Table[]
}

function buildBuckets(
  tables: Table[],
  groups: Record<string, Group>,
  noGroupLabel: string,
): GroupBucket[] {
  const grouped = new Map<string, Table[]>()

  for (const table of tables) {
    const key = table.g || NO_GROUP_KEY
    const items = grouped.get(key)
    if (items) items.push(table)
    else grouped.set(key, [table])
  }

  const buckets: GroupBucket[] = []

  for (const [key, bucketTables] of grouped.entries()) {
    if (!bucketTables.length) continue

    if (key === NO_GROUP_KEY) {
      buckets.push({
        key,
        group: { id: key, name: noGroupLabel, color: FALLBACK_GROUP_COLOR },
        tables: bucketTables,
      })
      continue
    }

    const group = groups[key] ?? { id: key, name: key, color: FALLBACK_GROUP_COLOR }
    buckets.push({ key, group, tables: bucketTables })
  }

  return buckets.sort((left, right) => {
    if (left.key === NO_GROUP_KEY) return 1
    if (right.key === NO_GROUP_KEY) return -1
    return left.group.name.localeCompare(right.group.name)
  })
}

function focusTableInCanvas(table: Table, sc: number, setPan: (tx: number, ty: number) => void) {
  const canvas = document.getElementById('cwrap')
  if (!canvas) return

  const rect = canvas.getBoundingClientRect()
  const nextTx = rect.width / (2 * sc) - (table.x + getTableWidth(table) / 2)
  const nextTy = rect.height / (2 * sc) - (table.y + getTableHeight(table) / 2)
  setPan(nextTx, nextTy)
}

export function calculateTableFocusViewport(
  table: Table,
  viewport: { width: number; height: number },
) {
  const tableWidth = getTableWidth(table)
  const tableHeight = getTableHeight(table)
  const scaleX = Math.max(1, viewport.width - TABLE_FOCUS_PADDING * 2) / tableWidth
  const scaleY = Math.max(1, viewport.height - TABLE_FOCUS_PADDING * 2) / tableHeight
  const nextScale = Math.max(
    SCALE_MIN,
    Math.min(TABLE_FOCUS_MAX_SCALE, scaleX, scaleY),
  )
  const nextTx = viewport.width / (2 * nextScale) - (table.x + tableWidth / 2)
  const nextTy = viewport.height / (2 * nextScale) - (table.y + tableHeight / 2)

  return { sc: nextScale, tx: nextTx, ty: nextTy }
}

function fitTableInCanvas(table: Table, setViewport: (sc: number, tx: number, ty: number) => void) {
  const canvas = document.getElementById('cwrap')
  if (!canvas) return

  const rect = canvas.getBoundingClientRect()
  const next = calculateTableFocusViewport(table, rect)
  setViewport(next.sc, next.tx, next.ty)
}

export function Sidebar() {
  const { t } = useTranslation()
  const tables = useDiagramStore((state) => state.tables)
  const groups = useDiagramStore((state) => state.groups)
  const rels = useDiagramStore((state) => state.rels)
  const selectedTableIds = useSelectionStore((state) => state.selectedTableIds)
  const selectOnlyTable = useSelectionStore((state) => state.selectOnlyTable)

  const sbCollapsed = useSettingsStore((state) => state.sbCollapsed)
  const toggleGroupCollapse = useSettingsStore((state) => state.toggleGroupCollapse)
  const sc = useViewportStore((state) => state.sc)
  const setViewport = useViewportStore((state) => state.setViewport)
  const setPan = useViewportStore((state) => state.setPan)
  const openEdit = useTableEditorModal((state) => state.openEdit)
  const showConfirm = useConfirmStore((state) => state.show)
  const deleteTable = useDiagramStore((state) => state.deleteTable)
  const activeTabId = useTabsStore((state) => state.activeTabId)
  const readOnly = useDraftStore((state) => state.mode === 'view')
  const hiddenTableIds = useTabViewStore((state) => state.hiddenTableIds)
  const hideTables = useTabViewStore((state) => state.hideTables)
  const showTables = useTabViewStore((state) => state.showTables)
  const toggleTableVisibility = useTabViewStore((state) => state.toggleTableVisibility)
  const [expandedTableIds, setExpandedTableIds] = useState<Record<string, boolean>>({})
  const [search, setSearch] = useState('')
  const hiddenTableIdSet = useMemo(() => new Set(hiddenTableIds), [hiddenTableIds])

  useEffect(() => {
    setSearch('')
  }, [activeTabId])

  const buckets = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    const filteredTables = normalizedSearch
      ? tables.filter((table) => {
          const haystack = `${table.schema} ${table.name}`.toLowerCase()
          return haystack.includes(normalizedSearch)
        })
      : tables

    return buildBuckets(filteredTables, groups, t('sidebar.noGroup'))
  }, [groups, search, t, tables])

  const handleToggleTable = (tableId: string) => {
    setExpandedTableIds((current) => ({
      ...current,
      [tableId]: !current[tableId],
    }))
  }

  const handleSelectTable = (table: Table) => {
    selectOnlyTable(table.id)
    fitTableInCanvas(table, setViewport)
  }

  const handleToggleVisibility = (table: Table) => {
    const wasHidden = hiddenTableIdSet.has(table.id)
    toggleTableVisibility(table.id)

    if (!wasHidden) {
      const selection = useSelectionStore.getState()
      if (selection.selectedTableId === table.id || selection.selectedTableIds.has(table.id)) {
        selection.clearSelection()
      }
      return
    }

    selectOnlyTable(table.id)
    const nextScale = Math.max(sc, 0.75)
    const canvas = document.getElementById('cwrap')
    if (!canvas) {
      focusTableInCanvas(table, nextScale, setPan)
      return
    }
    const rect = canvas.getBoundingClientRect()
    const nextTx = rect.width / (2 * nextScale) - (table.x + getTableWidth(table) / 2)
    const nextTy = rect.height / (2 * nextScale) - (table.y + getTableHeight(table) / 2)
    setViewport(nextScale, nextTx, nextTy)
  }

  const handleDeleteTable = (table: Table) => {
    if (readOnly) return
    showConfirm(
      t('prompts.deleteTableTitle'),
      t('contextMenu.deleteTableConfirm', { name: table.name }),
      () => {
        deleteTable(table.id)
        useSelectionStore.getState().clearSelection()
      },
    )
  }

  const handleColumnClick = (table: Table) => {
    selectOnlyTable(table.id)
    fitTableInCanvas(table, setViewport)
  }

  const getHiddenRelatedTableIds = (bucketTableIds: Set<string>): string[] => {
    const related = new Set<string>()
    for (const rel of rels) {
      if (bucketTableIds.has(rel.from) && !bucketTableIds.has(rel.to)) related.add(rel.to)
      if (bucketTableIds.has(rel.to) && !bucketTableIds.has(rel.from)) related.add(rel.from)
    }
    return [...related].filter((id) => hiddenTableIdSet.has(id))
  }

  return (
    <aside
      className="flex h-full min-h-0 w-60 shrink-0 flex-col overflow-hidden self-stretch"
      style={{
        background: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border)',
      }}
    >
      <div
        className="flex min-h-[44px] shrink-0 items-center justify-between px-3 text-[10px] font-bold tracking-widest"
        style={{ color: 'var(--text-dim)', borderBottom: '1px solid var(--border)' }}
      >
        <span>{t('sidebar.tables')}</span>
        <span style={{ color: 'var(--text-muted)' }}>{tables.length}</span>
      </div>

      <div className="shrink-0 border-b px-3 py-2" style={{ borderColor: 'var(--border)' }}>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('sidebar.searchPlaceholder')}
          className="morph-input h-8 text-[11px]"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain py-1">
        {!tables.length ? (
          <p className="px-3 py-4 text-[11px] italic" style={{ color: 'var(--text-dim)' }}>
            {t('sidebar.emptyTables')}
          </p>
        ) : buckets.length === 0 ? (
          <p className="px-3 py-4 text-[11px] italic" style={{ color: 'var(--text-dim)' }}>
            {t('sidebar.emptySearch')}
          </p>
        ) : (
          buckets.map((bucket) => {
            const bucketTableIds = new Set(bucket.tables.map((t) => t.id))
            const hiddenRelatedIds = getHiddenRelatedTableIds(bucketTableIds)
            return (
              <SidebarGroup
                key={bucket.key}
                groupId={bucket.key}
                group={bucket.group}
                tables={bucket.tables}
                allTables={tables}
                collapsed={!!sbCollapsed[bucket.key]}
                expandedTableIds={expandedTableIds}
                selectedTableIds={selectedTableIds}
                hiddenTableIdSet={hiddenTableIdSet}
                hasHiddenRelatedTables={hiddenRelatedIds.length > 0}
                onToggleGroup={() => toggleGroupCollapse(bucket.key)}
                onHideGroupTables={() => hideTables(bucket.tables.map((table) => table.id))}
                onShowGroupTables={() => showTables(bucket.tables.map((table) => table.id))}
                onShowRelatedGroupTables={() => showTables(hiddenRelatedIds)}
                onToggleTable={handleToggleTable}
                onSelectTable={handleSelectTable}
                onColumnClick={handleColumnClick}
                onToggleVisibility={handleToggleVisibility}
                onEditTable={(table) => {
                  if (!readOnly) openEdit(table.id)
                }}
                onDeleteTable={handleDeleteTable}
                readOnly={readOnly}
              />
            )
          })
        )}
      </div>
    </aside>
  )
}
